<template>
  <div class="edit-details-page">
    <v-container class="py-8">
      <!-- Header -->
      <div class="page-header mb-3">
        <div class="brand-header">
          <router-link to="/">
            <PassingOnLogo class="brand-logo-svg" :name2-override="store.personName ? '-' + store.personName : null" />
          </router-link>
        </div>

        <!-- Profile Menu (Top Right) -->
        <ProfileMenu />
      </div>

      <v-row justify="center">
        <v-col cols="12" lg="10">
          <v-card class="main-card" elevation="0">
            <v-card-title class="text-h6 section-header primary--text">
              Edit Memory Details
            </v-card-title>

            <v-card-text class="pa-6">
              <v-form ref="form" v-model="valid">
                <!-- Name -->
                <div class="mb-4">
                  <v-text-field
                    v-model="editableNameOfPerson"
                    label="Name of Person"
                    variant="outlined"
                    density="comfortable"
                    :rules="[v => !!v || 'Name is required']"
                    required
                  ></v-text-field>
                </div>

                <!-- Profile Photo Upload -->
                <div class="mb-6">
                  <div class="photo-upload-container">
                    <div v-if="!profilePhotoPreview && !existingProfilePhotoUrl" class="photo-upload-zone" @click="triggerPhotoUpload">
                      <v-icon size="48" color="primary" class="mb-2">mdi-camera-plus</v-icon>
                      <p class="text-body-2">Click to upload photo</p>
                      <input
                        ref="photoInput"
                        type="file"
                        accept="image/*,.heic,.HEIC"
                        style="display: none"
                        @change="handlePhotoUpload"
                      />
                    </div>

                    <div v-else class="photo-preview-container">
                      <img
                        :src="profilePhotoPreview || existingProfilePhotoUrl"
                        alt="Profile photo"
                        class="profile-photo-preview"
                      />
                      <v-btn
                        icon
                        size="default"
                        color="error"
                        class="remove-photo-btn"
                        @click="removeProfilePhoto"
                      >
                        <v-icon size="22">mdi-close</v-icon>
                      </v-btn>
                    </div>

                    <div class="photo-upload-info">
                      <p class="text-body-2">
                        Choose a photo of {{ editableNameOfPerson || 'the person for the memory' }}, this will be used for the digital memory and also when sending out invitations to contribute stories and add a personal touch to what can be seen as an impersonal email.
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Life dates -->
                <v-row class="mb-4">
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="editableBirthDate"
                      label="Date of Birth (Optional)"
                      type="date"
                      variant="outlined"
                      density="comfortable"
                      :max="editableDeathDate || today"
                      :rules="birthDateRules"
                      clearable
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" sm="6">
                    <v-text-field
                      v-model="editableDeathDate"
                      label="Date of Death (Optional)"
                      type="date"
                      variant="outlined"
                      density="comfortable"
                      :min="editableBirthDate || undefined"
                      :max="today"
                      :rules="deathDateRules"
                      clearable
                    ></v-text-field>
                  </v-col>
                </v-row>

                <!-- Event -->
                <div class="mb-4">
                  <p class="text-body-1 mb-2">Is there an event to remember {{ editableNameOfPerson || 'them' }}?</p>

                  <v-radio-group v-model="hasEventOption" class="mb-2">
                    <v-radio
                      label="Yes, there is an event"
                      value="yes"
                    ></v-radio>
                    <v-radio
                      label="No event at this time"
                      value="no"
                    ></v-radio>
                  </v-radio-group>

                  <v-alert
                    v-if="hasEventOption === 'no' && hadEventOnLoad"
                    type="warning"
                    variant="tonal"
                    density="compact"
                    class="mb-2"
                  >
                    Saving without an event will remove the event name, date, time, address and details, and RSVPs will no longer be offered.
                  </v-alert>
                </div>

                <v-expand-transition>
                  <div v-if="hasEventOption === 'yes'">
                    <!-- Event Name -->
                    <div class="mb-4">
                      <v-text-field
                        v-model="editableEventName"
                        label="Event Name (Optional)"
                        variant="outlined"
                        density="comfortable"
                        placeholder="e.g., Funeral Service, Wake, Celebration of Life"
                      ></v-text-field>
                    </div>

                    <!-- Memorial Date -->
                    <div class="mb-4">
                      <v-text-field
                        v-model="editableMemorialDate"
                        label="Event Date (Optional)"
                        type="date"
                        variant="outlined"
                        density="comfortable"
                      ></v-text-field>
                    </div>

                    <!-- Time -->
                    <v-row class="mb-4" no-gutters>
                      <v-col cols="6" class="pr-2">
                        <v-select
                          v-model="editableMemorialHour"
                          :items="hourItems"
                          label="Hour (Optional)"
                          variant="outlined"
                          density="comfortable"
                          clearable
                        ></v-select>
                      </v-col>
                      <v-col cols="6" class="pl-2">
                        <v-select
                          v-model="editableMemorialMinute"
                          :items="minuteItems"
                          label="Minute (Optional)"
                          variant="outlined"
                          density="comfortable"
                          clearable
                        ></v-select>
                      </v-col>
                    </v-row>

                    <!-- Address -->
                    <div class="mb-4">
                      <v-text-field
                        v-model="editableAddress"
                        label="Address (Optional)"
                        variant="outlined"
                        density="comfortable"
                        placeholder="e.g., St. Mary's Church, 123 Main Street, City"
                      ></v-text-field>
                    </div>

                    <!-- Additional Details -->
                    <div class="mb-4">
                      <v-textarea
                        v-model="editableDetails"
                        label="Additional Details (Optional)"
                        variant="outlined"
                        density="comfortable"
                        placeholder="e.g., Time: 2:00 PM, Dress code: Formal, Reception following at..."
                        rows="4"
                        auto-grow
                      ></v-textarea>
                    </div>
                  </div>
                </v-expand-transition>
              </v-form>
            </v-card-text>

            <v-card-actions class="pa-6 pt-0">
              <v-btn
                variant="text"
                @click="cancel"
                class="mr-4"
              >
                Back to Dashboard
              </v-btn>

              <v-spacer></v-spacer>

              <v-btn
                color="primary"
                size="large"
                rounded="0"
                elevation="0"
                :loading="saving"
                :disabled="!valid || saving"
                @click="save"
                class="px-8"
              >
                Save
                <v-icon right class="ml-2">mdi-content-save</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Error Dialog -->
    <v-dialog v-model="showErrorDialog" max-width="500">
      <v-card>
        <v-card-title>
          <v-icon color="error" class="mr-2">mdi-alert-circle</v-icon>
          Error
        </v-card-title>
        <v-card-text>
          <p>{{ errorMessage }}</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="showErrorDialog = false">OK</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useMemorialStore } from '../stores/memorial'
import apiService from '../services/api'
import { isSupportedImage, normalizeImageFile, readFileAsDataUrl } from '../services/photoUpload'
import ProfileMenu from '../components/ProfileMenu.vue'

const router = useRouter()
const store = useMemorialStore()

const form = ref(null)
const valid = ref(false)
const saving = ref(false)
const editableNameOfPerson = ref('')
const editableBirthDate = ref('')
const editableDeathDate = ref('')
const hasEventOption = ref('no')
const hadEventOnLoad = ref(false)
const editableEventName = ref('')
const editableMemorialDate = ref('')
const editableMemorialHour = ref(null)
const editableMemorialMinute = ref(null)
const editableAddress = ref('')
const editableDetails = ref('')

// Today, used to stop a birthdate being set in the future
const today = new Date().toISOString().split('T')[0]

const birthDateRules = [
  v => !v || v <= today || 'Date of birth cannot be in the future',
  v => !v || !editableDeathDate.value || v <= editableDeathDate.value || 'Date of birth must be before date of death'
]

const deathDateRules = [
  v => !v || v <= today || 'Date of death cannot be in the future',
  v => !v || !editableBirthDate.value || v >= editableBirthDate.value || 'Date of death must be after date of birth'
]

const hourItems = Array.from({ length: 24 }, (_, i) => ({
  title: String(i).padStart(2, '0'),
  value: i
}))

const minuteItems = Array.from({ length: 60 }, (_, i) => ({
  title: String(i).padStart(2, '0'),
  value: i
}))

const photoInput = ref(null)
const profilePhotoFile = ref(null)
const profilePhotoPreview = ref(null)
const existingProfilePhoto = ref(null)
const existingProfilePhotoUrl = ref(null)

const showErrorDialog = ref(false)
const errorMessage = ref('')

const triggerPhotoUpload = () => {
  photoInput.value?.click()
}

const handlePhotoUpload = async (event) => {
  const file = event.target.files?.[0]
  if (!file) return

  if (!isSupportedImage(file)) {
    console.error('Invalid file type. Please select an image.')
    return
  }

  const maxSize = 10 * 1024 * 1024
  if (file.size > maxSize) {
    console.error('File size exceeds 10MB limit.')
    return
  }

  try {
    const processedFile = await normalizeImageFile(file)
    profilePhotoFile.value = processedFile
    existingProfilePhoto.value = null

    profilePhotoPreview.value = await readFileAsDataUrl(processedFile)
  } catch (error) {
    console.error('Error processing file:', error)
  }
}

const removeProfilePhoto = () => {
  profilePhotoFile.value = null
  profilePhotoPreview.value = null
  existingProfilePhoto.value = null
  existingProfilePhotoUrl.value = null
  if (photoInput.value) {
    photoInput.value.value = ''
  }
}

const cancel = () => {
  router.push('/memory/dashboard')
}

const save = async () => {
  if (!valid.value || saving.value) return

  saving.value = true

  try {
    let profilePhotoUuid = undefined

    // Determine profile photo state
    if (profilePhotoFile.value) {
      // User uploaded a new photo - upload it first
      const photoResponse = await apiService.uploadPhoto(
        store.memorialUuid,
        profilePhotoFile.value,
        '',
        store.apiToken,
        {
          isProfilePhoto: true
        }
      )

      if (photoResponse.success && photoResponse.media?.uuid) {
        profilePhotoUuid = photoResponse.media.uuid
      }
    } else if (!existingProfilePhotoUrl.value && !existingProfilePhoto.value) {
      // Photo was removed - send empty string to clear it
      profilePhotoUuid = ''
    }
    // If photo exists and wasn't changed, don't send profilePhotoUuid (leave as undefined)

    const hasEvent = hasEventOption.value === 'yes'

    const memorialData = {
      uuid: store.memorialUuid,
      nameOfPerson: editableNameOfPerson.value,
      // Empty string clears the stored date, null would leave it untouched
      birthDate: editableBirthDate.value || '',
      deathDate: editableDeathDate.value || '',
      hasEvent,
      eventName: hasEvent ? (editableEventName.value || '') : '',
      memorialDate: hasEvent ? (editableMemorialDate.value || null) : null,
      memorialHour: hasEvent ? (editableMemorialHour.value ?? null) : null,
      memorialMinute: hasEvent ? (editableMemorialMinute.value ?? null) : null,
      address: hasEvent ? (editableAddress.value || null) : null,
      details: hasEvent ? (editableDetails.value || null) : null
    }

    // Only include profilePhotoUuid if it was changed (new upload or removal)
    if (profilePhotoUuid !== undefined) {
      memorialData.profilePhotoUuid = profilePhotoUuid
    }

    const response = await apiService.editMemorial(memorialData, store.apiToken)

    if (!response.success) {
      throw new Error(response.msg || 'Failed to save memory details')
    }

    // Reload memorial data
    await store.loadUserMemorialFromServer()

    router.push('/memory/dashboard')
  } catch (error) {
    console.error('Save error:', error)
    errorMessage.value = 'Failed to save memory details. Please try again.'
    showErrorDialog.value = true
  } finally {
    saving.value = false
  }
}

onMounted(async () => {
  if (store.memorialUuid && store.apiToken) {
    await store.loadUserMemorialFromServer()

    editableNameOfPerson.value = store.personName || ''
    editableBirthDate.value = store.birthDate || ''
    editableDeathDate.value = store.deathDate || ''
    editableEventName.value = store.eventName || ''
    editableMemorialDate.value = store.memorialDate || ''
    editableMemorialHour.value = store.memorialHour ?? null
    editableMemorialMinute.value = store.memorialMinute ?? null
    editableAddress.value = store.memorialAddress || ''
    editableDetails.value = store.memorialDetails || ''

    hadEventOnLoad.value = !!store.hasEvent
    hasEventOption.value = store.hasEvent ? 'yes' : 'no'

    if (store.profilePhoto) {
      existingProfilePhoto.value = store.profilePhoto
      existingProfilePhotoUrl.value = store.profilePhoto.url
    }
  }
})
</script>

<style lang="scss" scoped>
.edit-details-page {
  min-height: 100vh;
  background: var(--v-theme-background);
}

.page-header {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  /* symmetric gutters reserve room for the profile menu pinned to the right, so
     a long memorial name truncates before it can run underneath the icon */
  padding-left: 64px;
  padding-right: 64px;
}

.brand-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-width: 0;
  max-width: 100%;
}

/* The router-link wrapper has to be shrinkable too, otherwise the name never
   truncates; flex also removes the inline baseline gap under the brand text
   that was pushing the profile icon out of alignment with the title. */
.brand-header > a {
  display: flex;
  align-items: center;
  min-width: 0;
  max-width: 100%;
}

.brand-logo {
  height: 40px;
  width: auto;
}

.brand-title {
  letter-spacing: 0.1rem;
  font-weight: 400;
  color: rgb(var(--v-theme-primary));
  margin: 0;
}

.profile-menu-wrapper {
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
}

.section-header {
  font-weight: 600 !important;
  border-bottom: 1px solid var(--v-theme-border);
}

.main-card {
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.photo-upload-container {
  display: flex;
  gap: 2rem;
  align-items: flex-start;
}

.photo-upload-zone {
  border: 2px dashed rgba(var(--v-theme-primary), 0.3);
  border-radius: 8px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  background: rgba(var(--v-theme-primary), 0.02);
  width: 300px;
  height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    border-color: rgba(var(--v-theme-primary), 0.6);
    background: rgba(var(--v-theme-primary), 0.05);
  }
}

.photo-preview-container {
  position: relative;
  width: 300px;
  height: 300px;
  flex-shrink: 0;
}

.profile-photo-preview {
  width: 100%;
  height: 100%;
  border-radius: 8px;
  object-fit: cover;
  display: block;
}

.remove-photo-btn {
  position: absolute;
  top: 8px;
  right: 8px;
  background: var(--v-theme-surface) !important;
  border-radius: 999px;
  min-width: 36px;
  width: 36px;
  height: 36px;
  border: 1px solid rgba(15, 23, 42, 0.2);
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.12);
}

.photo-upload-info {
  flex: 1;
  display: flex;
  align-items: center;

  p {
    color: var(--v-theme-gray500);
    line-height: 1.6;
  }
}

@media (max-width: 768px) {
  .photo-upload-container {
    flex-direction: column;
  }

  .photo-upload-zone,
  .photo-preview-container {
    width: 100%;
    max-width: 300px;
  }
}
</style>
